/**
 * Format a date string into a readable format
 * 
 * @param {string|Date} dateString - The date to format
 * @param {Object} options - Formatting options
 * @returns {string} The formatted date string
 */
export const formatDate = (dateString, options = {}) => {
  if (!dateString) return 'N/A';
  
  try {
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    // Default format options
    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: options.includeTime ? '2-digit' : undefined,
      minute: options.includeTime ? '2-digit' : undefined,
    };
    
    // Merge default options with provided options
    const formatOptions = { ...defaultOptions, ...options };
    
    return new Intl.DateTimeFormat('en-US', formatOptions).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Error';
  }
};

/**
 * Format a date string to a localized date and time
 * @param {string} dateString - The date string to format
 * @returns {string} - Formatted date and time string
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  
  return new Date(dateString).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Format a currency value
 * 
 * @param {number} value - The value to format as currency
 * @param {string} currency - The currency code (default: USD)
 * @returns {string} The formatted currency string
 */
export const formatCurrency = (value, currency = 'USD') => {
  if (value === null || value === undefined) return 'N/A';
  
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch (error) {
    console.error('Error formatting currency:', error);
    return `${value}`;
  }
};

/**
 * Format request status for display
 * @param {string} status - The status to format
 * @returns {string} - Formatted status text
 */
export const formatStatus = (status, requestType) => {
  if (!status) return 'Unknown';

  const isOutbound = ['ship', 'pick_pack', 'kit_build'].includes(requestType);
  const isInboundManifest = requestType === 'inbound_manifest';

  const statusMap = {
    'draft': 'Draft',
    'pending': 'Pending',
    'approved': isOutbound ? 'Pending Shipment' : 'Approved',
    'pending_receive': isInboundManifest ? 'Pending Receipt' : 'Scheduled',
    'in_progress': isInboundManifest ? 'Partially Received' : 'In Progress',
    'completed': isInboundManifest ? 'Complete' : 'Completed',
    'cancelled': 'Cancelled',
    'rejected': 'Rejected'
  };

  return statusMap[status] || status.charAt(0).toUpperCase() + status.slice(1);
};

/**
 * Get appropriate color for a status badge
 * @param {string} status - The status
 * @returns {Object} - Object with color classes for the badge
 */
export const getStatusColor = (status) => {
  if (!status) return { bg: 'bg-gray-100', text: 'text-gray-800' };
  
  const colorMap = {
    'draft': { bg: 'bg-gray-100', text: 'text-gray-800' },
    'pending': { bg: 'bg-yellow-100', text: 'text-yellow-800' },
    'approved': { bg: 'bg-blue-100', text: 'text-blue-800' },
    'in_progress': { bg: 'bg-indigo-100', text: 'text-indigo-800' },
    'completed': { bg: 'bg-green-100', text: 'text-green-800' },
    'cancelled': { bg: 'bg-red-100', text: 'text-red-800' },
    'rejected': { bg: 'bg-red-100', text: 'text-red-800' },
    'paid': { bg: 'bg-green-100', text: 'text-green-800' },
    'overdue': { bg: 'bg-red-100', text: 'text-red-800' },
    'sent': { bg: 'bg-blue-100', text: 'text-blue-800' }
  };
  
  return colorMap[status] || { bg: 'bg-gray-100', text: 'text-gray-800' };
};

/**
 * Format a phone number to (XXX) XXX-XXXX format
 * @param {string} phone - The phone number to format
 * @returns {string} - Formatted phone number
 */
export const formatPhoneNumber = (phone) => {
  if (!phone) return 'N/A';
  
  // Remove all non-numeric characters
  const cleaned = ('' + phone).replace(/\D/g, '');
  
  // Format the phone number
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return '(' + match[1] + ') ' + match[2] + '-' + match[3];
  }
  
  return phone;
};

/**
 * Truncate text to a maximum length and add ellipsis
 * 
 * @param {string} text - The text to truncate
 * @param {number} maxLength - The maximum length
 * @returns {string} The truncated text
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Format a number with commas for thousands separator
 * 
 * @param {number} value - The number to format
 * @param {number} decimals - The number of decimal places to show
 * @returns {string} The formatted number
 */
export const formatNumber = (value, decimals = 0) => {
  if (value === null || value === undefined) return 'N/A';
  
  try {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  } catch (error) {
    console.error('Error formatting number:', error);
    return `${value}`;
  }
};
